import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Page, PageHeader, ConsoleTabs, Tag, CopyLine } from '../components/console/ui';

const INSTALL_CMD =
  'curl -fsSL https://github.com/Podstack-ai/podstack-cli-releases/releases/latest/download/install.sh | sh';

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="ps-card flex items-start gap-3.5 p-5" aria-labelledby={`connect-step-${n}`}>
      <span className="ps-num">{n}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <h2 id={`connect-step-${n}`} className="m-0 text-base font-medium text-ps-text">
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

const desc = 'm-0 text-[13px] leading-relaxed text-ps-muted';

export default function Connect() {
  const [params] = useSearchParams();
  const id = params.get('id') || '<instance-id>';

  return (
    <Page>
      <PageHeader
        title="Connect"
        subtitle="Connect to your training instances from the podstack CLI. For security, the portal offers no in-browser SSH or terminal - connections happen only from your own machine."
      />
      <ConsoleTabs />

      <div className="ps-promo border-[#1F4A2E] bg-[#0B1A11]" role="note">
        <Tag tone="ok">CLI-only</Tag>
        <span className="flex-[1_1_260px] text-ps-muted">
          Your SSH keys never leave your device, and there is no browser terminal to compromise.
        </span>
      </div>

      <Step n={1} title="Install the CLI">
        <p className={desc}>Install the podstack CLI on your machine (macOS / Linux).</p>
        <CopyLine code={INSTALL_CMD} />
      </Step>

      <Step n={2} title="Authenticate">
        <p className={desc}>
          Log in with an API key. Create one on{' '}
          <Link to="/console/keys" className="ps-link">
            Keys &amp; SSH
          </Link>{' '}
          (it starts with <code className="rounded bg-ps-raised px-1.5 py-0.5 font-mono text-ps-text">psk_</code>), then
          paste it when prompted.
        </p>
        <CopyLine code="podstack auth login" />
      </Step>

      <Step n={3} title="Connect to your instance">
        <p className={desc}>Open an SSH session to a running instance by its id.</p>
        <CopyLine code={`podstack gpu instances ssh ${id}`} />
      </Step>

      <section className="ps-card flex flex-col gap-2.5 p-5" aria-labelledby="connect-more">
        <h2 id="connect-more" className="m-0 text-base font-medium text-ps-text">
          More commands
        </h2>
        <p className={desc}>You can also copy files and expose ports.</p>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-ps-subtle">Copy files up</span>
          <CopyLine code={`podstack gpu instances cp ./data ${id}:/workspace/data`} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-ps-subtle">Expose a port (e.g. Jupyter)</span>
          <CopyLine code={`podstack gpu instances expose ${id} 8888`} />
        </div>
      </section>

      <p className="m-0 text-xs text-ps-subtle">
        Manage your instances on the{' '}
        <Link to="/console/instances" className="ps-link">
          Instances
        </Link>{' '}
        page.
      </p>
    </Page>
  );
}
